// =============================================================================
// countdown.ts – Real-time countdown ticker for home departure times
// =============================================================================

/**
 * Starts a live countdown ticker for all DOM elements with `data-leave-ts` under `rootEl`.
 * Updates their text content and urgency CSS classes every second.
 * Returns a cleanup function to stop the interval.
 */
export function startCountdown(rootEl: HTMLElement): () => void {
  const tick = () => {
    const nodes = rootEl.querySelectorAll<HTMLElement>("[data-leave-ts]");
    const state = (window as any)._app_state;
    const now = Date.now() + (state?.simulatedTimeActive ? (state.simulatedTimeOffset || 0) : 0);
    nodes.forEach((el) => {
      const targetMs = Number(el.dataset.leaveTs);
      if (isNaN(targetMs) || targetMs <= 0) return;
      const secsLeft = Math.round((targetMs - now) / 1000);
      el.textContent = formatCountdown(secsLeft);

      // Toggle CSS urgency classes
      // imminent: <= 2 minutes (120 seconds) and > 0
      el.classList.toggle("countdown-imminent", secsLeft > 0 && secsLeft <= 120);
      // now: <= 0
      el.classList.toggle("countdown-now", secsLeft <= 0);
    });
  };

  tick();
  const intervalId = setInterval(tick, 1000);
  return () => clearInterval(intervalId);
}

/**
 * Formats seconds left into a countdown string.
 * Format: "tra X min" (if > 5 min), "M:SS" (if <= 5 min), "ESCI ORA" (if <= 0).
 */
export function formatCountdown(secsLeft: number): string {
  if (secsLeft <= 0) {
    return "ESCI ORA";
  }
  if (secsLeft > 300) {
    // Greater than 5 minutes -> "tra X min"
    const mins = Math.ceil(secsLeft / 60);
    return `tra ${mins} min`;
  }
  const m = Math.floor(secsLeft / 60);
  const s = secsLeft % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
